import type {
  CreateQuestionRequest,
  QuestionDetail,
  QuestionDetailTranslation,
  QuestionOptionDetail,
  QuestionOptionTranslation,
  UpdateQuestionBody,
} from '@e-learning/core';
import type {
  CreateQuestionFormData,
  FetchQuestionsParams,
  QuestionBankAdminToast,
  QuestionListItem,
} from '@e-learning/shared';
import {
  CREATE_QUESTION_MAX_OPTIONS,
  CREATE_QUESTION_MIN_OPTIONS,
  CreateQuestionModal,
  DynamicModal,
  LoadingModal,
  QuestionBank,
  QuestionBankAdminTabs,
  Toast,
  useTheme,
  useTranslation,
} from '@e-learning/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCourses } from '../Courses/hooks';
import { useQuestions } from './hooks';
import { useQuizConfig } from './hooks/useQuizConfig';

/** API usa DifficultyLevel como integer: 0=Low, 1=Medium, 2=High. Labels vêm do i18n. */

/** Formato retornado por fetchCourseCategories (v1/CourseCategory) – mesmo do Courses para o filtro All Categories */
interface CourseCategoryItem {
  id: string;
  translations: { languageCode: string; name: string }[];
}

/** Mapeia a resposta de GET v1/Question/{id} para o formulário (CreateQuestionFormData). */
function questionDetailToFormData(
  detail: QuestionDetail
): CreateQuestionFormData {
  const enTranslation = detail.translations.find(
    (t: QuestionDetailTranslation) => t.languageCode === 'en-US'
  );
  const esTranslation = detail.translations.find(
    (t: QuestionDetailTranslation) => t.languageCode === 'es-ES'
  );
  const questionText = {
    en: enTranslation?.title ?? '',
    es: esTranslation?.title ?? '',
  };
  const explanation = {
    en: enTranslation?.explanation ?? '',
    es: esTranslation?.explanation ?? '',
  };
  let options = (detail.questionOptions ?? []).map(
    (opt: QuestionOptionDetail) => ({
      en:
        opt.translations.find(
          (tr: QuestionOptionTranslation) => tr.languageCode === 'en-US'
        )?.description ?? '',
      es:
        opt.translations.find(
          (tr: QuestionOptionTranslation) => tr.languageCode === 'es-ES'
        )?.description ?? '',
    })
  );
  if (options.length < CREATE_QUESTION_MIN_OPTIONS) {
    options = [
      ...options,
      ...Array.from(
        { length: CREATE_QUESTION_MIN_OPTIONS - options.length },
        () => ({
          en: '',
          es: '',
        })
      ),
    ];
  } else if (options.length > CREATE_QUESTION_MAX_OPTIONS) {
    options = options.slice(0, CREATE_QUESTION_MAX_OPTIONS);
  }
  const correctIndex =
    detail.questionOptions?.findIndex(
      (o: QuestionOptionDetail) => o.isCorrect
    ) ?? 0;
  const correctOptionIndex = Math.max(
    0,
    Math.min(correctIndex, options.length - 1)
  );
  return {
    questionText,
    difficultyLevel: String(detail.difficultyLevel),
    categoryId: detail.courseCategoryId ?? '',
    linkedCourseIds: detail.linkedCourseIds ?? [],
    options,
    correctOptionIndex,
    explanation,
  };
}

/** Retorna os códigos de idioma informados (com título da questão preenchido). Pelo menos 1 é obrigatório (validação do form). */
function getInformedLanguageCodes(
  data: CreateQuestionFormData
): ('en-US' | 'es-ES')[] {
  const codes: ('en-US' | 'es-ES')[] = [];
  if ((data.questionText?.en ?? '').trim()) codes.push('en-US');
  if ((data.questionText?.es ?? '').trim()) codes.push('es-ES');
  return codes.length > 0 ? codes : ['en-US'];
}

function formDataToCreateQuestionRequest(
  data: CreateQuestionFormData
): CreateQuestionRequest {
  const languageCodes = getInformedLanguageCodes(data);
  const qText = data.questionText ?? { en: '', es: '' };
  const translations = languageCodes.map(lc => ({
    languageCode: lc,
    title: (lc === 'en-US' ? qText.en : qText.es).trim() || '',
  }));

  const options = (data.options ?? []).map(opt => {
    const o = opt ?? { en: '', es: '' };
    return languageCodes.map(lc => ({
      languageCode: lc as 'en-US' | 'es-ES',
      text: (lc === 'en-US' ? o.en : o.es).trim() || '',
    }));
  });

  const expl = data.explanation ?? { en: '', es: '' };
  const explanationEntries = languageCodes
    .map(lc => ({
      languageCode: lc,
      text: (lc === 'en-US' ? expl.en : expl.es).trim(),
    }))
    .filter(e => e.text !== '');
  const explanation =
    explanationEntries.length > 0 ? explanationEntries : undefined;

  return {
    translations,
    difficultyLevel: parseInt(data.difficultyLevel, 10) || 0,
    courseCategoryId: data.categoryId?.trim() ?? '',
    linkedCourseIds:
      data.linkedCourseIds?.length > 0 ? data.linkedCourseIds : undefined,
    options,
    correctOptionIndex: Math.max(
      0,
      Math.min(data.correctOptionIndex, options.length - 1)
    ),
    explanation,
  };
}

/** Monta o body do PUT v1/Question/{id} a partir do form e dos ids das opções (carregados na edição). Envia apenas os idiomas informados. */
function formDataToUpdateQuestionBody(
  data: CreateQuestionFormData,
  optionIds: string[]
): UpdateQuestionBody {
  const languageCodes = getInformedLanguageCodes(data);
  const qText = data.questionText ?? { en: '', es: '' };
  const expl = data.explanation ?? { en: '', es: '' };
  const translations = languageCodes.map(lc => ({
    languageCode: lc,
    title: (lc === 'en-US' ? qText.en : qText.es).trim() || '',
    explanation: (lc === 'en-US' ? expl.en : expl.es).trim() || '',
  }));
  const questionOptions = (data.options ?? []).map((opt, i) => {
    const o = opt ?? { en: '', es: '' };
    return {
      id: optionIds[i] ?? '',
      isCorrect: i === data.correctOptionIndex,
      translations: languageCodes.map(lc => ({
        languageCode: lc,
        description: (lc === 'en-US' ? o.en : o.es).trim() || '',
      })),
    };
  });
  return {
    courseCategoryId: data.categoryId?.trim() ?? '',
    difficultyLevel: parseInt(data.difficultyLevel, 10) || 0,
    translations,
    questionOptions,
    linkedCourseIds: data.linkedCourseIds?.length
      ? data.linkedCourseIds
      : undefined,
  };
}

export default function QuestionBankPage() {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const {
    fetchQuestions: fetchQuestionsApi,
    getQuestion,
    createQuestion,
    updateQuestion,
    deleteQuestion,
  } = useQuestions();
  const { fetchCourseCategories, fetchCourses } = useCourses();
  const { fetchQuizConfig, updateQuizConfig } = useQuizConfig();

  const [categoryList, setCategoryList] = useState<CourseCategoryItem[]>([]);
  const [courseList, setCourseList] = useState<{ id: string; name: string }[]>(
    []
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [editingQuestionId, setEditingQuestionId] = useState<string | null>(
    null
  );
  const [editingQuestionOptionIds, setEditingQuestionOptionIds] = useState<
    string[]
  >([]);
  const [editFormData, setEditFormData] =
    useState<CreateQuestionFormData | null>(null);
  const [loadingQuestion, setLoadingQuestion] = useState(false);
  const [listKey, setListKey] = useState(0);
  const [saving, setSaving] = useState(false);
  const [questionToDelete, setQuestionToDelete] =
    useState<QuestionListItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [toastMessage, setToastMessage] = useState<{
    text: string;
    variant: 'success' | 'error';
  } | null>(null);

  /** Adapta parâmetros do shared para a API do core (Title, DifficultyLevel, CourseCategoryId). */
  const fetchQuestions = useCallback(
    async (params: FetchQuestionsParams) => {
      // Garante que o ESLint enxergue que esta callback depende do idioma,
      // mantendo o comportamento de "recarregar" quando o language mudar.
      void i18n.language;
      const difficultyLevel =
        params.difficulty !== undefined && params.difficulty !== ''
          ? Number(params.difficulty)
          : undefined;
      return fetchQuestionsApi({
        page: params.page,
        limit: params.limit,
        includeInactive: false,
        title: params.search,
        difficultyLevel: Number.isNaN(difficultyLevel)
          ? undefined
          : difficultyLevel,
        courseCategoryId: params.categoryId || undefined,
      });
    },
    // Recarrega a lista paginada quando o idioma mudar,
    // pois a API pode retornar campos já localizados via Accept-Language.
    [fetchQuestionsApi, i18n.language]
  );

  const difficultyOptions = useMemo(
    () => [
      { value: '0', label: t('questionBank.create.difficulty_low') },
      { value: '1', label: t('questionBank.create.difficulty_medium') },
      { value: '2', label: t('questionBank.create.difficulty_high') },
    ],
    [t]
  );

  const desiredLanguageCode = i18n.language === 'es' ? 'es-ES' : 'en-US';

  const categoryOptions = useMemo(
    () =>
      categoryList.map(c => ({
        value: c.id,
        // A API retorna `translations` com vários `languageCode`.
        // Não podemos assumir que `[0]` é sempre o idioma selecionado no front.
        label:
          c.translations?.find(tr => {
            return tr.languageCode === desiredLanguageCode;
          })?.name ??
          c.translations?.[0]?.name ??
          c.id,
      })),
    [categoryList, desiredLanguageCode]
  );

  const courseOptions = useMemo(
    () =>
      courseList.map(c => ({
        value: c.id,
        label: c.name || c.id,
      })),
    [courseList]
  );

  useEffect(() => {
    let cancelled = false;
    fetchCourseCategories({ includeInactive: false })
      .then((list: CourseCategoryItem[]) => {
        if (!cancelled) setCategoryList(list);
      })
      .catch(() => {
        if (!cancelled) setCategoryList([]);
      });
    return () => {
      cancelled = true;
    };
    // Recarrega apenas o dropdown de categorias quando trocar o idioma,
    // pois o backend/localização pode retornar `translations` já filtradas por `Accept-Language`.
  }, [fetchCourseCategories, i18n.language]);

  useEffect(() => {
    if (!modalOpen) return;
    let cancelled = false;
    fetchCourses({ page: 1, limit: 200 })
      .then(res => {
        if (!cancelled && res?.data)
          setCourseList(
            res.data.map((c: { id: string; name?: string }) => ({
              id: c.id,
              name: c.name ?? c.id,
            }))
          );
      })
      .catch(() => {
        if (!cancelled) setCourseList([]);
      });
    return () => {
      cancelled = true;
    };
  }, [modalOpen, fetchCourses]);

  useEffect(() => {
    if (!toastMessage) return;
    const id = setTimeout(() => setToastMessage(null), 4000);
    return () => clearTimeout(id);
  }, [toastMessage]);

  /** Referência estável para `QuestionBankAdminTabs` (evita re-disparar GET quiz-config a cada render). */
  const handleQuestionBankToast = useCallback((toast: QuestionBankAdminToast) => {
    setToastMessage(toast);
  }, []);

  const handleCreateQuestion = useCallback(() => {
    setEditingQuestionId(null);
    setEditingQuestionOptionIds([]);
    setEditFormData(null);
    setModalOpen(true);
  }, []);

  useEffect(() => {
    if (!modalOpen || !editingQuestionId) return;
    let cancelled = false;
    setLoadingQuestion(true);
    setEditFormData(null);
    getQuestion(editingQuestionId)
      .then((detail: QuestionDetail) => {
        if (!cancelled) {
          setEditFormData(questionDetailToFormData(detail));
          setEditingQuestionOptionIds(
            (detail.questionOptions ?? []).map(o => o.id)
          );
        }
      })
      .catch(() => {
        if (!cancelled) {
          setToastMessage({
            text:
              t('questionBank.create.load_question_error') ??
              'Erro ao carregar questão',
            variant: 'error',
          });
          setModalOpen(false);
          setEditingQuestionId(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingQuestion(false);
      });
    return () => {
      cancelled = true;
    };
  }, [modalOpen, editingQuestionId, getQuestion, t]);

  const handleSaveQuestion = useCallback(
    async (data: CreateQuestionFormData) => {
      if (saving) return;
      setSaving(true);
      setToastMessage(null);
      try {
        if (editingQuestionId != null) {
          const body = formDataToUpdateQuestionBody(
            data,
            editingQuestionOptionIds
          );
          await updateQuestion(editingQuestionId, body);
        } else {
          const request = formDataToCreateQuestionRequest(data);
          await createQuestion(request);
        }
        setModalOpen(false);
        setEditingQuestionId(null);
        setEditingQuestionOptionIds([]);
        setEditFormData(null);
        setListKey(k => k + 1);
        setToastMessage({
          text: t('questionBank.create.save_success'),
          variant: 'success',
        });
      } catch {
        setToastMessage({
          text: t('questionBank.create.save_error'),
          variant: 'error',
        });
      } finally {
        setSaving(false);
      }
    },
    [
      createQuestion,
      updateQuestion,
      editingQuestionId,
      editingQuestionOptionIds,
      saving,
      t,
    ]
  );

  const handleEdit = useCallback((question: QuestionListItem) => {
    setEditingQuestionId(question.id);
    setModalOpen(true);
  }, []);

  const handleDelete = useCallback((question: QuestionListItem) => {
    setQuestionToDelete(question);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!questionToDelete || isDeleting) return;
    setIsDeleting(true);
    setToastMessage(null);
    try {
      await deleteQuestion(questionToDelete.id);
      setQuestionToDelete(null);
      setListKey(k => k + 1);
      setToastMessage({
        text: t('questionBank.delete_success'),
        variant: 'success',
      });
    } catch {
      setToastMessage({
        text: t('questionBank.delete_error'),
        variant: 'error',
      });
    } finally {
      setIsDeleting(false);
    }
  }, [questionToDelete, isDeleting, deleteQuestion, t]);

  return (
    <div data-e2e="page-admin-question-bank">
      {toastMessage && (
        <Toast
          message={toastMessage.text}
          variant={toastMessage.variant}
          role="alert"
        />
      )}
      <LoadingModal open={saving} message={t('questionBank.create.saving')} />

      <div
        style={{
          padding: theme.spacing.lg,
          paddingBottom: theme.spacing.md,
          backgroundColor: theme.palette.background.default,
        }}
      >
        <h1
          style={{
            margin: 0,
            marginBottom: theme.spacing.xs,
            fontWeight: theme.typography.fontWeight.bold,
            fontSize: theme.typography.fontSize['2xl'],
            color: theme.palette.text.primary,
            fontFamily: theme.typography.fontFamily,
          }}
        >
          {t('questionBank.title')}
        </h1>
        <p
          style={{
            margin: 0,
            marginBottom: theme.spacing.md,
            fontSize: theme.typography.fontSize.base,
            color: theme.palette.text.secondary,
            fontFamily: theme.typography.fontFamily,
          }}
        >
          {t('questionBank.subtitle')}
        </p>

        <QuestionBankAdminTabs
          initialTab="questions"
          questions={
            <QuestionBank
              key={listKey}
              fetchQuestions={fetchQuestions}
              difficultyOptions={difficultyOptions}
              categoryOptions={categoryOptions}
              onCreateQuestion={handleCreateQuestion}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          }
          onToast={handleQuestionBankToast}
          fetchQuizConfig={fetchQuizConfig}
          updateQuizConfig={updateQuizConfig}
        />
      </div>

      <CreateQuestionModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingQuestionId(null);
          setEditingQuestionOptionIds([]);
          setEditFormData(null);
        }}
        onSave={handleSaveQuestion}
        difficultyOptions={difficultyOptions}
        categoryOptions={categoryOptions}
        courseOptions={courseOptions}
        saving={saving}
        initialData={
          editingQuestionId != null && editFormData != null
            ? editFormData
            : null
        }
        loading={editingQuestionId != null && loadingQuestion}
      />
      <DynamicModal
        title={t('questionBank.delete_confirm_title')}
        description={t('questionBank.delete_confirm_message')}
        compact
        open={!!questionToDelete}
        onClose={() => !isDeleting && setQuestionToDelete(null)}
        closeOnOverlayClick={false}
        fields={[]}
        actions={[
          {
            label: t('questionBank.create.cancel'),
            onClick: () => setQuestionToDelete(null),
            variant: 'secondary',
          },
          {
            label: isDeleting
              ? t('questionBank.modal_action_deleting')
              : t('questionBank.modal_action_delete'),
            onClick: handleConfirmDelete,
            variant: 'primary',
          },
        ]}
        dataE2ePrefix="question-bank-delete-confirm"
      />
    </div>
  );
}
